<template>
  <div class="contact--profile">
    <div class="contact--info">
      <div class="contact-info--header">
        <div class="contact-info--thumbnail">
          <thumbnail
            v-if="showAvatar"
            :src="contact.thumbnail"
            size="56px"
            :username="contact.name"
            :status="contact.availability_status"
          />

          <div v-if="contact.blocked_contact" class="blocked-badge">
            {{ $t('BLOCK_CONTACT_MODAL.BADGE') }}
          </div>
        </div>

        <woot-button
          v-if="showCloseButton"
          :icon="closeIconName"
          class="clear secondary close-button--rtl"
          @click="onPanelToggle"
        />
      </div>

      <slot name="mobile-actions"></slot>

      <div class="contact--details">
        <div v-if="showAvatar" class="contact--name-wrap">
          <h3 class="sub-block-title contact--name">
            {{ contact.name }}
          </h3>
        </div>

        <p v-if="additionalAttributes.description" class="contact--bio">
          {{ additionalAttributes.description }}
        </p>

        <social-icons :social-profiles="socialProfiles" />
      </div>

      <div class="contact-actions">
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.NEW_MESSAGE')"
          title="$t('CONTACT_PANEL.NEW_MESSAGE')"
          class="new-message"
          icon="chat"
          size="small"
          @click="toggleConversationModal"
        />
        <woot-button
          v-if="canWriteContacts"
          v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
          title="$t('EDIT_CONTACT.BUTTON_LABEL')"
          class="edit-contact"
          icon="edit"
          variant="smooth"
          size="small"
          @click="toggleEditModal"
        />
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
          title="$t('CONTACT_PANEL.MERGE_CONTACT')"
          class="merge-contact"
          icon="merge"
          variant="smooth"
          size="small"
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <woot-button
          v-tooltip="$t('CONTACT_PANEL.MERGE_CONVERSATION')"
          title="$t('CONTACT_PANEL.MERGE_CONVERSATION')"
          class="merge-conversation"
          icon="box"
          variant="smooth"
          size="small"
          :disabled="uiFlags.isMerging"
          @click="openConversationMergeModal"
        />
        <a
          :href="contactProfileLink"
          class="fs-default"
          target="_blank"
          rel="noopener nofollow noreferrer"
          v-tooltip="$t('CONTACTS_PAGE.LIST.VIEW_DETAILS')"
        >
          <woot-button
            class="edit-contact"
            icon="open"
            variant="smooth"
            size="small"
          />
        </a>

        <woot-button
          v-if="currentChat && !currentChat.muted"
          v-tooltip="$t('CONTACT_PANEL.MUTE_CONTACT')"
          class="muted-contact"
          icon="speaker-mute"
          variant="smooth"
          size="small"
          @click="mute"
        />
        <woot-button
          v-else-if="currentChat"
          v-tooltip.left="$t('CONTACT_PANEL.UNMUTE_CONTACT')"
          class="muted-contact"
          icon="speaker-1"
          variant="smooth"
          size="small"
          @click="unmute"
        />
        <woot-button
          v-if="currentChat"
          v-tooltip="$t('CONTACT_PANEL.SEND_TRANSCRIPT')"
          class="muted-contact"
          icon="share"
          variant="smooth"
          size="small"
          @click="toggleEmailActionsModal"
        />

        <woot-button
          v-if="currentChat"
          v-tooltip="
            contact.blocked_contact
              ? $t('BLOCK_CONTACT_MODAL.TOOLTIP.UNBLOCK')
              : $t('BLOCK_CONTACT_MODAL.TOOLTIP.BLOCK')
          "
          class="blocked-contact"
          icon="lock-closed"
          variant="smooth"
          size="small"
          :style="contact.blocked_contact ? 'color: red;' : ''"
          @click="showBlockModal = true"
        />

        <woot-button
          v-if="showDelete"
          v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
          title="$t('DELETE_CONTACT.BUTTON_LABEL')"
          class="delete-contact"
          icon="delete"
          variant="smooth"
          size="small"
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>

      <div class="contact--metadata">
        <contact-info-row
          v-if="contact.email || contact.identifier"
          :href="contact.email ? `mailto:${contact.email}` : ''"
          :value="contact.email || contact.identifier"
          :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
          :showCopy="true"
        />
        <contact-info-row
          :href="
            contact.phone_number
              ? `tel:${contact.phone_number}`
              : parseNumber(contact.phone_number)
          "
          :value="parseNumber(contact.phone_number)"
          :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
          :showCopy="true"
        />
        <contact-info-row
          v-if="additionalAttributes.company_name"
          :value="additionalAttributes.company_name"
          :title="$t('CONTACT_PANEL.COMPANY')"
        />
        <contact-info-row
          v-if="location || additionalAttributes.location"
          :value="location || additionalAttributes.location"
          :title="$t('CONTACT_PANEL.LOCATION')"
        />

        <p class="created-at">
          {{ $t('CONTACT_PANEL.CREATED_AT_LABEL') }}:
          <span v-tooltip="createAtInfo.tooltip">{{ createAtInfo.text }}</span>
        </p>
      </div>

      <email-transcript-modal
        v-if="showEmailActionsModal"
        :show="showEmailActionsModal"
        :current-chat="currentChat"
        @cancel="toggleEmailActionsModal"
      />

      <edit-contact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />

      <new-conversation
        v-if="contact.id"
        :show="showConversationModal"
        :contact="contact"
        @cancel="toggleConversationModal"
      />

      <contact-merge-modal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="toggleMergeModal"
      />

      <conversation-merge-modal
        v-if="showConversationMergeModal"
        :primary-contact="contact"
        :show="showConversationMergeModal"
        @close="toggleConversationMergeModal"
      />

      <contact-block-modal
        v-if="showBlockModal"
        :show.sync="showBlockModal"
        :contact="contact"
        :blocked="contact.blocked_contact"
        @confirm="confirmBlockContact"
      />
    </div>

    <woot-delete-modal
      v-if="showDeleteModal"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import timeMixin from 'dashboard/mixins/time';
import ContactBlockModal from './ContactBlockModal';
import ContactInfoRow from './ContactInfoRow';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import SocialIcons from './SocialIcons';
import EmailTranscriptModal from 'dashboard/components/widgets/conversation/EmailTranscriptModal.vue';
import EditContact from './EditContact';
import NewConversation from './NewConversation';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal';
import ConversationMergeModal from 'dashboard/modules/conversations/ConversationMergeModal';
import alertMixin from 'shared/mixins/alertMixin';
import agentMixin from 'dashboard/mixins/agentMixin';
import contactWritePermission from 'dashboard/mixins/contactWritePermission';
import { mapGetters } from 'vuex';
import { getCountryFlag } from 'dashboard/helper/flag';

export default {
  components: {
    ContactInfoRow,
    EditContact,
    Thumbnail,
    SocialIcons,
    NewConversation,
    ContactMergeModal,
    ConversationMergeModal,
    EmailTranscriptModal,
    ContactBlockModal,
  },
  mixins: [agentMixin, alertMixin, clickaway, timeMixin, contactWritePermission],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    channelType: {
      type: String,
      default: '',
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    closeIconName: {
      type: String,
      default: 'dismiss',
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showEditModal: false,
      showConversationModal: false,
      showMergeModal: false,
      showDeleteModal: false,
      showConversationMergeModal: false,
      showEmailActionsModal: false,
      showBlockModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      currentChat: 'getSelectedChat',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
      } = this.additionalAttributes;

      return { twitter: twitterScreenName, ...(socialProfiles || {}) };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    createAtInfo() {
      if (!this.contact || !this.contact.created_at) {
        return { tooltip: '', text: '---' };
      }
      const date = this.contact.created_at;
      const info = {tooltip: this.messageStamp(new Date(date), 'dd/MM/yy - HH:mm'), text: ""};
      if(this.messageStamp(date, 'dd/MM/yy') === this.messageStamp(Math.floor(Date.now() / 1000), 'dd/MM/yy')){
        info.text = this.longTimestamp(this.dynamicTime(new Date(date)))
      } else {
        info.text = this.messageStamp(new Date(date), 'dd/MM/yy - HH:mm');
      }
      return info;
    },
  },
  methods: {
    toggleEmailActionsModal() {
      this.showEmailActionsModal = !this.showEmailActionsModal;
    },
    mute() {
      this.$store.dispatch('muteConversation', this.currentChat.id);
      this.showAlert(this.$t('CONTACT_PANEL.MUTED_SUCCESS'));
    },
    unmute() {
      this.$store.dispatch('unmuteConversation', this.currentChat.id);
      this.showAlert(this.$t('CONTACT_PANEL.UNMUTED_SUCCESS'));
    },
    toggleConversationMergeModal() {
      this.showConversationMergeModal = !this.showConversationMergeModal;
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    onPanelToggle() {
      this.$emit('toggle-panel');
    },
    toggleConversationModal() {
      this.showConversationModal = !this.showConversationModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        const countryFlag = countryCode ? getCountryFlag(countryCode) : '🌎';
        return `${cityAndCountry} ${countryFlag}`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panel-close');
        this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));
        if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({ name: 'contacts_dashboard' });
        }
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    openMergeModal() {
      this.toggleMergeModal();
    },
    openConversationMergeModal() {
      this.toggleConversationMergeModal();
    },
    parseNumber(number) {
      if (
        number &&
        !number.includes('tel') &&
        this.agentRules?.mask_number === false
      ) {
        const sub = number.substring(5, number.length - 4);
        return number.replace(sub, '*'.repeat(sub.length)) || '';
      }

      return number || '';
    },
    async confirmBlockContact() {
      try {
        const newValue = !this.contact.blocked_contact;

        this.showBlockModal = false;

        await this.$store.dispatch('contacts/update', {
          id: this.contact.id,
          blocked_contact: newValue,
          blocked_from_templates: newValue,
          blocked_from_messages: newValue,
        });

        this.contact.blocked_contact = newValue;
        this.contact.blocked_from_templates = newValue;

        if (this.currentChat?.meta?.sender?.id === this.contact.id) {
          this.$set(this.currentChat.meta.sender, 'blocked_contact', newValue);

          this.$set(
            this.currentChat.meta.sender,
            'blocked_from_templates',
            newValue
          );

          this.$set(
            this.currentChat.meta.sender,
            'blocked_from_messages',
            newValue
          );
        }

        this.showAlert(
          newValue
            ? this.$t('BLOCK_CONTACT_MODAL.ALERT.BLOCK_SUCCESS')
            : this.$t('BLOCK_CONTACT_MODAL.ALERT.UNBLOCK_SUCCESS')
        );
      } catch (error) {
        this.showBlockModal = true;

        this.showAlert(
          error?.response?.message ||
            this.$t('BLOCK_CONTACT_MODAL.ALERT.UPDATE_ERROR')
        );
      }
    },
  },
  mounted() {
    if (!this.agentList.length) {
      this.$store.dispatch('agents/get');
    }
  },
};
</script>

<style scoped lang="scss">
.contact--profile {
  position: relative;
  align-items: flex-start;
}

.contact--details {
  margin-top: var(--space-medium);
  width: 100%;
  padding: var(--space-normal);
}

.contact--info {
  text-align: left;
}

.contact-info--header {
  background-color: #053ca8;
  height: var(--space-larger);

  .contact-info--thumbnail {
    position: relative;
    display: flex;
    justify-content: center;
    .user-thumbnail-box {
      margin-top: var(--space-two);
    }
    .blocked-badge {
      position: absolute;
      top: calc(var(--space-two) - 4px);
      background: #dc2626;
      color: #fff;
      font-size: 0.9rem;
      font-weight: 700;
      letter-spacing: 0.08rem;
      padding: 0.25rem 0.85rem;
      border-radius: 999px;
      z-index: 2;
      pointer-events: none;
      text-transform: uppercase;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    }
  }

  .close-button--rtl {
    top: var(--space-micro);
    right: var(--space-micro);
    position: absolute;
    color: var(--white);

    &:hover {
      background-color: transparent;
      color: var(--white-transparent);
    }
  }
}

.contact--name-wrap {
  text-align: center;
  margin-bottom: var(--space-small);
}

.contact--name {
  text-transform: capitalize;
  white-space: normal;
  margin: 0 var(--space-smaller) 0 var(--space-smaller);

  a {
    color: var(--color-body);
  }
}

.contact--metadata {
  text-align: center;
  margin-top: var(--space-two);

  .created-at {
    color: var(--b-500);
    margin-top: var(--space-small);
    font-size: var(--font-size-micro);
    font-weight: var(--font-weight-normal);
  }
}

.contact-actions {
  justify-content: center;
  display: flex;
  align-items: center;
  width: 100%;

  .new-message,
  .edit-contact,
  .merge-contact,
  .merge-conversation,
  .delete-contact,
  .muted-contact,
  .blocked-contact {
    margin-right: var(--space-small);
  }
}

.merge-summary--card {
  padding: var(--space-normal);
}

.contact--bio {
  word-wrap: break-word;
}

.button--contact-menu {
  position: absolute;
  right: var(--space-normal);
  top: 0;
}
</style>
